import { BRAND_KEY_AFH_HUB } from "../../../constants/brandConstants";

export type AuthRoleOption = {
  value: string;
  label: string;
  description: string;
  canSelectFacilityType: boolean;
};

export type AuthFacilityTypeOption = {
  value: string;
  label: string;
};

export type AuthMode = "legacy_cognito_role" | "multi_role_context";

export type AuthExperienceConfig = {
  brandKey: string;
  brandName: string;
  host?: string;
  authMode: AuthMode;
  featureFlags: {
    allowMultiRole: boolean;
    allowRoleSwitching: boolean;
    allowFacilitySwitching: boolean;
    dailyBehaviorLogReuseRecentEntries: {
      enabled: boolean;
      excludedFacilityTypes: string[];
    };
    providerTools: {
      eFax: boolean;
      residentScreening: boolean;
      entryTracker: boolean;
    };
  };
  company: {
    companyName: string;
    registeredState: string;
    supportEmail: string;
    supportPhone: string;
    supportHours: string;
  };
  shell: {
    footerDescription: string;
    contactDialogSubtitle: string;
  };
  auth: {
    signInSubtitle: string;
    signUpSubtitle: string;
    forgotPasswordSubtitle: string;
    confirmRegistrationSubtitle: string;
    autoConfirmSubtitle: string;
  };
  publicSite: {
    platformDomain: string;
    heroTitle: string;
    heroSubtitle: string;
    builtForTitle: string;
    builtForDescription: string;
    hoursSavedBadge: string;
    hoursSavedDescription: string;
  };
  vocabulary: {
    facilitySingular: string;
    facilityPlural: string;
    facilityPluralLower: string;
    facilityAbbreviation: string;
    facilityAbbreviationPlural: string;
    operatorPlural: string;
    providerPlural: string;
    operationsLabel: string;
    websiteLabel: string;
    websiteLabelPlural: string;
  };
  roleOptions: AuthRoleOption[];
  facilityTypeOptions: AuthFacilityTypeOption[];
  providerSignup: {
    fieldLabel: string;
    defaultFacilityType: string;
    editable: boolean;
  };
  providerHome: {
    detailsTitle: string;
    detailsSubtitle: string;
    profileSectionTitle: string;
    profileSectionSubtitle: string;
    websiteSectionTitle: string;
    websiteSectionSubtitle: string;
    addHomeTitle: string;
    addHomeSubtitle: string;
    homeNameLabel: string;
    homeAddressLabel: string;
    homeNameRequiredMessage: string;
    facilityTypeLabel: string;
  };
  discovery: {
    listingsTitle: string;
    listingsSubtitle: string;
    listingsWorkspaceSubtitle: string;
    listingsResultLabel: string;
    jobWebsiteLabel: string;
    homeNameLabel: string;
  };
  nurseWorkspace: {
    workspaceDescription: string;
    providerLabel: string;
    providerPluralLabel: string;
    providerNameLabel: string;
    providerAddressLabel: string;
    findProviderTitle: string;
    findProviderSubtitle: string;
    searchBrandLabel: string;
    providerFoundMessage: string;
    providerNotFoundMessage: string;
    notProviderMessage: string;
    saveProviderMessage: string;
    enterProviderDetailsMessage: string;
    archiveProviderHeader: string;
    existingBrandUserNotProviderMessage: string;
    existingProviderMessage: string;
  };
};

export const DEFAULT_AUTH_EXPERIENCE: AuthExperienceConfig = {
  brandKey: BRAND_KEY_AFH_HUB,
  brandName: "AFH Hub",
  authMode: "legacy_cognito_role",
  featureFlags: {
    allowMultiRole: false,
    allowRoleSwitching: false,
    allowFacilitySwitching: false,
    dailyBehaviorLogReuseRecentEntries: { enabled: true, excludedFacilityTypes: ["esf"] },
    providerTools: { eFax: false, residentScreening: false, entryTracker: false },
  },
  company: {
    companyName: "Adult Family Home Hub LLC",
    registeredState: "Washington",
    supportEmail: "support@afhhub.com",
    supportPhone: "(425) 586-0918",
    supportHours: "Monday through Friday, from 9:00 AM to 5:00 PM (PST)",
  },
  shell: {
    footerDescription: "AFH Hub is a platform for care operations. We help providers manage care-plan workflows, documents, reminders, websites, and day-to-day facility management more efficiently.",
    contactDialogSubtitle: "Reach the AFH Hub team for product, billing, or platform questions.",
  },
  auth: {
    signInSubtitle: "Sign in to continue to your AFH Hub workspace.",
    signUpSubtitle: "Create an account to get started and choose the facility type that fits your operations.",
    forgotPasswordSubtitle: "We'll send a reset code to the email associated with your AFH Hub account.",
    confirmRegistrationSubtitle: "Use the confirmation link from your email, or enter the verification code below if needed.",
    autoConfirmSubtitle: "We are confirming your registration and signing you in if your browser session is still available.",
  },
  publicSite: {
    platformDomain: "AFHHub.com",
    heroTitle: "The Operating System for AFHs",
    heroSubtitle: "Simplifying AFH Care & Compliance",
    builtForTitle: "One workflow, less manual entry",
    builtForDescription: "Bring resident care, review, hiring, and staff management into one organized workspace.",
    hoursSavedBadge: "Hours Saved with AFH Hub",
    hoursSavedDescription: "These estimates reflect the difference between building resident documentation manually versus using AFH Hub automation, CBHS/IBSS Tracker behavior documentation, monthly behavior reports, and connected workflow support.",
  },
  vocabulary: {
    facilitySingular: "Adult Family Home",
    facilityPlural: "Adult Family Homes",
    facilityPluralLower: "adult family homes",
    facilityAbbreviation: "AFH",
    facilityAbbreviationPlural: "AFHs",
    operatorPlural: "Adult Family Home operators",
    providerPlural: "Adult Family Home providers",
    operationsLabel: "Adult Family Home operations",
    websiteLabel: "AFH website",
    websiteLabelPlural: "AFH websites",
  },
  roleOptions: [
    { label: "AFH Provider", value: "provider", description: "Manage facilities, residents, staff, care operations, and workspace settings.", canSelectFacilityType: true },
    { label: "Caregiver", value: "caregiver", description: "Support residents with daily care activities, routine documentation, and care tasks.", canSelectFacilityType: false },
    { label: "Nurse", value: "nurse", description: "Support clinical care, assessments, care plans, delegation, and resident follow-up.", canSelectFacilityType: false },
  ],
  facilityTypeOptions: [
    { value: "afh", label: "Adult Family Home (AFH)" },
    { value: "esf", label: "Enhanced Services Facility (ESF)" },
  ],
  providerSignup: {
    fieldLabel: "Facility Type",
    defaultFacilityType: "afh",
    editable: false,
  },
  providerHome: {
    detailsTitle: "Home Details",
    detailsSubtitle: "Manage the AFH profile, public website, residents, and linked caregivers from one workspace.",
    profileSectionTitle: "AFH Profile",
    profileSectionSubtitle: "Review your core AFH details, documents, and website access at a glance.",
    websiteSectionTitle: "AFH Website",
    websiteSectionSubtitle: "Manage your public AFH website, visitor inquiries, and shareable links.",
    addHomeTitle: "Add Home",
    addHomeSubtitle: "Let's set up your first AFH.",
    homeNameLabel: "AFH Name",
    homeAddressLabel: "AFH Address",
    homeNameRequiredMessage: "AFH Name is required.",
    facilityTypeLabel: "Facility Type",
  },
  discovery: {
    listingsTitle: "AFH Listings",
    listingsSubtitle: "Search neighboring adult family homes with vacancies and open full listing details.",
    listingsWorkspaceSubtitle: "Search nearby adult family homes and review vacancy details from a single dashboard section.",
    listingsResultLabel: "AFH listings",
    jobWebsiteLabel: "AFH Website",
    homeNameLabel: "AFH Name",
  },
  nurseWorkspace: {
    workspaceDescription: "Manage your AFH providers and their residents",
    providerLabel: "AFH Provider",
    providerPluralLabel: "AFH Providers",
    providerNameLabel: "AFH Provider Name",
    providerAddressLabel: "AFH Address",
    findProviderTitle: "Find the AFH provider first",
    findProviderSubtitle: "Type the AFH provider email and click Search.",
    searchBrandLabel: "Searching AFH Hub...",
    providerFoundMessage: "Provider found in AFH Hub and can be linked to your workspace.",
    providerNotFoundMessage: "Provider not found in AFH Hub.",
    notProviderMessage: "This is not an AFH provider email",
    saveProviderMessage: "Click Save to add this AFH provider.",
    enterProviderDetailsMessage: "Enter the AFH provider details below.",
    archiveProviderHeader: "Archive AFH Provider",
    existingBrandUserNotProviderMessage: "This email already belongs to an AFH Hub user, but it is not registered as a provider.",
    existingProviderMessage: "This provider is already in AFH Hub. Click Save.",
  },
};
